import { createClient } from 'contentful';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { useState, useEffect } from 'react';

if (!process.env.REACT_APP_CONTENTFUL_SPACE_ID || !process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN) {
  console.error('Missing Contentful environment variables');
  throw new Error('Missing required Contentful environment variables');
}

export const contentfulClient = createClient({
  space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
});

console.log('Contentful client initialized with:', {
  spaceId: process.env.REACT_APP_CONTENTFUL_SPACE_ID?.substring(0, 4) + '...',
  accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN?.substring(0, 4) + '...'
});

export async function getBlogPosts() {
  const response = await contentfulClient.getEntries({
    content_type: 'blogPost',
    order: '-fields.publishDate',
  });

  return response.items.map(item => ({
    slug: item.fields.slug,
    title: item.fields.title,
    excerpt: item.fields.excerpt,
    coverImage: `https:${item.fields.coverImage?.fields?.file?.url}` || '',
    date: item.fields.publishDate,
    author: item.fields.author,
    content: item.fields.content,
    seoDescription: item.fields.seoDescription,
    relatedPosts: item.fields.relatedPosts?.map(post => post.fields.slug) || []
  }));
}

export async function getBlogPost(slug) {
  const response = await contentfulClient.getEntries({
    content_type: 'blogPost',
    'fields.slug': slug,
    limit: 1,
  });

  if (!response.items.length) {
    return null;
  }

  const post = response.items[0];
  return {
    slug: post.fields.slug,
    title: post.fields.title,
    excerpt: post.fields.excerpt,
    coverImage: `https:${post.fields.coverImage?.fields?.file?.url}` || '',
    date: post.fields.publishDate,
    author: post.fields.author,
    content: post.fields.content,
    seoDescription: post.fields.seoDescription,
    relatedPosts: post.fields.relatedPosts?.map(p => p.fields.slug) || []
  };
}

export function RentalConditions() {
  const [sections, setSections] = useState([]);
  const [openSection, setOpenSection] = useState(null);

  useEffect(() => {
    const client = createClient({
      space: process.env.REACT_APP_CONTENTFUL_SPACE_ID,
      accessToken: process.env.REACT_APP_CONTENTFUL_ACCESS_TOKEN,
    });
    
    client
      .getEntries({ content_type: 'rentalCondition' })
      .then((response) => {
        const fetchedSections = response.items.map((item) => ({
          id: item.sys.id,
          title: item.fields.title,
          content: item.fields.content,
        }));
        setSections(fetchedSections);
        if (fetchedSections.length > 0) {
          setOpenSection(fetchedSections[0].id); // open the first section by default
        }
      })
      .catch(console.error);
  }, []);

  const toggleSection = (sectionId) => {
    setOpenSection(openSection === sectionId ? null : sectionId);
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 bg-white">
      <h1 className="text-5xl font-bold text-gray-900 mb-16 text-center">
        Condiciones de alquiler
      </h1>
      <div className="space-y-6">
        {sections.map((section, index) => (
          <section
            key={section.id}
            className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden"
          >
            <button
              onClick={(e) => {
                e.preventDefault();
                toggleSection(section.id);
              }}
              className="w-full p-8 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
            >
              <h2 className="text-3xl font-bold text-gray-900 flex items-center">
                <span className="text-brand mr-3">{index + 1}.</span>
                {section.title}
              </h2>
              <svg
                className={`w-6 h-6 text-gray-400 transform transition-transform ${
                  openSection === section.id ? 'rotate-180' : ''
                }`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`transition-all duration-300 ease-in-out ${
                openSection === section.id ? 'max-h-[2000px] opacity-100' : 'max-h-0 opacity-0'
              } overflow-hidden`}
            >
              <div className="p-8 pt-0 space-y-8">
                {typeof section.content === 'string'
                  ? section.content.split('\n').map((line, i) => (
                      <p key={i} className="text-gray-600 leading-relaxed">{line}</p>
                    ))
                  : documentToReactComponents(section.content)}
              </div>
            </div>
          </section>
        ))}
      </div>
    </div>
  );
} 